import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button } from "react-bootstrap";
import { capitalizeFirstLetter, formatDateTime } from "../utils/Helper";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import PaginatedTable from "../components/Table/PaginatedTable";
import UserModal from "../components/Modal/CreateUserModal";

const UserManagement = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const { setAccessToken, accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalUser, setModalUser] = useState(null); // null = create, object = edit

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  /* ================= Fetch Users ================= */
  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = [
        `limit=${limit}`,
        `page=${page}`,
      ];
      if (keyword) queryParams.push(`q=${encodeURIComponent(keyword)}`);
      const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

      const response = await APIServices.get(`admin/users${queryString}`, headers);

      if (response?.status === 200) {
        const data = response?.data?.results || [];
        setUsers(data);

        const totalItems = response?.data?.total || data.length;
        setTotalCount(Math.ceil(totalItems / limit));
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setUsers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, limit, keyword]);

  /* ================= Toggle User Active Status ================= */
  const toggleStatus = async (user) => {
    const newStatus = !user.is_active;
    try {
      const response = await APIServices.put(
        `admin/users/${user.user_id}`,
        { ...user, is_active: newStatus },
        headers
      );

      if (response?.status === 200) {
        setUsers((prev) =>
          prev.map((u) =>
            u.user_id === user.user_id ? { ...u, is_active: newStatus } : u
          )
        );
        toast.success(
          `${user.username} is now ${newStatus ? "Enabled" : "Disabled"}`
        );
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to update status", error);
      toast.error("Failed to update status");
    }
  };

  /* ================= Table Columns ================= */
  const columns = [
    {
      dataField: "username",
      text: "Username",
      formatter: (cell) => capitalizeFirstLetter(cell),
    },
    {
      dataField: "user_login",
      text: "User Login",
    },
    {
      dataField: "user_role",
      text: "Role",
    },
    {
      dataField: "is_active",
      text: "Status",
      formatter: (cell, row) => (
        <span
          className={`status-badge ${cell ? "enabled" : "disabled"}`}
          onClick={() => toggleStatus(row)}
          style={{ cursor: "pointer" }}
        >
          {cell ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    {
      dataField: "creation_datetime",
      text: "Created At",
      formatter: (cell) => formatDateTime(cell),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <span
          style={{ cursor: "pointer" }}
          onClick={() => {
            setModalUser(row);
            setShowModal(true);
          }}
        >
          {/* SVG Edit Icon */}
          <svg
            className="svg-inline--fa fa-pencil-alt fa-w-16 clr_grey me-2"
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="pencil-alt"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            width="16"
            height="16"
          >
            <path
              fill="currentColor"
              d="M497.9 142.1l-46.1 46.1c-4.7 4.7-12.3 4.7-17 0l-111-111c-4.7-4.7-4.7-12.3 0-17l46.1-46.1c18.7-18.7 49.1-18.7 67.9 0l60.1 60.1c18.8 18.7 18.8 49.1 0 67.9zM284.2 99.8L21.6 362.4.4 483.9c-2.9 16.4 11.4 30.6 27.8 27.8l121.5-21.3 262.6-262.6c4.7-4.7 4.7-12.3 0-17l-111-111c-4.8-4.7-12.4-4.7-17.1 0zM124.1 339.9c-5.5-5.5-5.5-14.3 0-19.8l154-154c5.5-5.5 14.3-5.5 19.8 0s5.5 14.3 0 19.8l-154 154c-5.5 5.5-14.3 5.5-19.8 0zM88 424h48v36.3l-64.5 11.3-31.1-31.1L51.7 376H88v48z"
            ></path>
          </svg>
        </span>
      ),
    },
  ];

  return (
    <div className="all_content user_management_sec">
      <div className="container-fluid">
        {/* ===== Page Header ===== */}
        <div className="head-section d-flex align-items-center justify-content-between">
          <h1 className="font_16 font-600 clr_blue">User Management</h1>
          <button
            className="btn sky_theme btn-sm gap-2"
            onClick={() => {
              setModalUser(null); // create mode
              setShowModal(true);
            }}
          >
            <i className="fa-solid fa-plus"></i> Create User
          </button>
        </div>

        {/* ===== Search ===== */}
        <div className="search-box d-flex align-items-center gap-3 mt-4">
          <div className="search_input position-relative">
            <span className="search_icon">
              <i className="fa-solid fa-magnifying-glass"></i>
            </span>
            <input
              type="text"
              className="form-control h-40"
              placeholder="Search by username or email"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchUsers(1);
              }}
            />
          </div>
          <div className="btn_sec d-flex align-items-center gap-2">
            <button
              className="btn sky_theme btn-sm h-40"
              onClick={() => fetchUsers(1)}
            >
              Search
            </button>
            <button
              className="btn d-flex align-items-center gap-2 font_14 clr_sky"
              onClick={() => {
                setKeyword("");
                fetchUsers(1);
              }}
            >
              <i className="fas fa-redo-alt"></i> Reset
            </button>
          </div>
        </div>

        {/* ===== User Table ===== */}
        <div className="comman_table user_management_table mt-4">
          <div className="table-responsive">
            {loading ? (
              <h2 className="text-center mb-4">Loading...</h2>
            ) : (
              <PaginatedTable
                data={users}
                columns={columns}
                totalPages={totalCount}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                limit={limit}
                setLimit={setLimit}
              />
            )}
          </div>
        </div>
      </div>

      {/* ===== Create/Edit User Modal ===== */}
      <UserModal
        show={showModal}
        onClose={() => setShowModal(false)}
        user={modalUser}
        APIServices={APIServices}
        headers={headers}
        onSuccess={(message) => {
          toast.success(message);
          fetchUsers(currentPage);
        }}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default UserManagement;
